import React from 'react'
import { Box, styled } from '@mui/material'

const Row = styled(Box)({
  display: 'flex',
  gap: 24,
  padding: '18px 0',
  borderTop: '1px solid var(--ds-border)',
  '&:first-of-type': { borderTop: 'none', paddingTop: 0 },
})

const Label = styled(Box)({
  width: 160,
  flexShrink: 0,
  paddingTop: 9,
})

const Title = styled('div')({
  fontSize: 13.5,
  fontWeight: 640,
  color: 'var(--ds-ink)',
})

const SubLabel = styled('div')({
  fontSize: 11.5,
  color: 'var(--ds-ink-subtle)',
  marginTop: 3,
})

const Content = styled(Box)({
  flex: 1,
  minWidth: 0,
})

export interface SettingsFieldProps {
  label: React.ReactNode
  hint?: React.ReactNode
  children: React.ReactNode
}

/**
 * Shared `sx` for `<Input variant='filled' />` inside settings forms — matches
 * `11-settings.html`'s white/bordered field treatment (`box-shadow: inset 0 0 0 1px
 * var(--line-strong)`, violet focus ring, 40px tall single-line fields) instead of
 * the DS default gray-filled look, which is taller (its `12px 16px` padding plus
 * MUI's own input padding renders ~56px tall — too tall for a dense settings form).
 */
export const fieldInputSx = {
  height: 40,
  padding: '0 13px',
  fontSize: 13.5,
  backgroundColor: 'var(--ds-canvas)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
  '& .MuiInputBase-input': { padding: 0, fontSize: 13.5 },
  '&.Mui-focused': {
    boxShadow: 'inset 0 0 0 1.5px var(--ds-accent-deep-b)',
  },
}

/** Same field treatment as `fieldInputSx`, sized for the multiline bio textarea. */
export const fieldTextareaSx = {
  padding: '10px 13px',
  fontSize: 13.5,
  backgroundColor: 'var(--ds-canvas)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border-strong)',
  '& .MuiInputBase-input': { padding: 0, fontSize: 13.5 },
  '&.Mui-focused': {
    boxShadow: 'inset 0 0 0 1.5px var(--ds-accent-deep-b)',
  },
}

/** Label + description + content row — settings form field pattern. */
export function SettingsField({ label, hint, children }: SettingsFieldProps) {
  return (
    <Row>
      <Label>
        <Title>{label}</Title>
        {hint && <SubLabel>{hint}</SubLabel>}
      </Label>
      <Content>{children}</Content>
    </Row>
  )
}
